// shadcn/ui-style primitives (same names, props and token system as shadcn),
// implemented with plain CSS because Tailwind is not wired into this CRA build.
// Theme a design by setting the CSS variables on a wrapper via <ThemeScope vars={{...}}>.
import { forwardRef } from "react";

const CSS = `
.ui-scope{--background:#fff;--foreground:#0f172a;--card:#fff;--card-foreground:#0f172a;--primary:#0f172a;--primary-foreground:#f8fafc;--secondary:#f1f5f9;--secondary-foreground:#0f172a;--muted:#f1f5f9;--muted-foreground:#64748b;--accent:#f1f5f9;--destructive:#ef4444;--destructive-foreground:#fff;--border:#e2e8f0;--input:#e2e8f0;--ring:#94a3b8;--radius:.5rem;font-family:'IBM Plex Sans',system-ui,sans-serif;color:var(--foreground);box-sizing:border-box}
.ui-scope *,.ui-scope *::before,.ui-scope *::after{box-sizing:inherit}
.ui-btn{display:inline-flex;align-items:center;justify-content:center;gap:.5rem;white-space:nowrap;border:1px solid transparent;border-radius:calc(var(--radius) - 2px);font:inherit;font-size:.875rem;font-weight:500;height:2.5rem;padding:0 1rem;cursor:pointer;transition:background-color .15s,box-shadow .15s,opacity .15s,transform .1s;outline:none}
.ui-btn:focus-visible{box-shadow:0 0 0 2px var(--background),0 0 0 4px var(--ring)}
.ui-btn:disabled{pointer-events:none;opacity:.5}
.ui-btn:active{transform:translateY(1px)}
.ui-btn-default{background:var(--primary);color:var(--primary-foreground)}.ui-btn-default:hover{opacity:.9}
.ui-btn-destructive{background:var(--destructive);color:var(--destructive-foreground)}.ui-btn-destructive:hover{opacity:.9}
.ui-btn-outline{background:var(--background);border-color:var(--input);color:var(--foreground)}.ui-btn-outline:hover{background:var(--accent)}
.ui-btn-secondary{background:var(--secondary);color:var(--secondary-foreground)}.ui-btn-secondary:hover{opacity:.8}
.ui-btn-ghost{background:transparent;color:var(--foreground)}.ui-btn-ghost:hover{background:var(--accent)}
.ui-btn-link{background:transparent;color:var(--primary);text-decoration:underline;text-underline-offset:4px;padding:0;height:auto}
.ui-btn-sm{height:2.25rem;padding:0 .75rem;border-radius:calc(var(--radius) - 2px)}
.ui-btn-lg{height:2.75rem;padding:0 2rem;border-radius:var(--radius)}
.ui-btn-icon{height:2.5rem;width:2.5rem;padding:0}
.ui-input{display:flex;height:2.5rem;width:100%;border-radius:calc(var(--radius) - 2px);border:1px solid var(--input);background:var(--background);color:var(--foreground);padding:0 .75rem;font:inherit;font-size:.875rem;outline:none;transition:box-shadow .15s,border-color .15s}
.ui-input{-webkit-text-fill-color:var(--foreground);caret-color:var(--foreground);opacity:1;background-clip:padding-box}
.ui-input::placeholder{color:var(--muted-foreground);-webkit-text-fill-color:var(--muted-foreground);opacity:1}
.ui-input:-webkit-autofill,.ui-input:-webkit-autofill:hover,.ui-input:-webkit-autofill:focus{-webkit-text-fill-color:var(--foreground);caret-color:var(--foreground);-webkit-box-shadow:0 0 0 1000px var(--background) inset;transition:background-color 9999s ease-out 0s}
.ui-input:focus-visible{box-shadow:0 0 0 2px var(--background),0 0 0 4px var(--ring)}
.ui-input:disabled{cursor:not-allowed;opacity:.5}
.ui-label{font-size:.875rem;font-weight:500;line-height:1;display:block}
.ui-card{border-radius:var(--radius);border:1px solid var(--border);background:var(--card);color:var(--card-foreground);box-shadow:0 1px 2px rgba(0,0,0,.05)}
.ui-card-header{display:flex;flex-direction:column;gap:.375rem;padding:1.5rem}
.ui-card-title{font-size:1.5rem;font-weight:600;line-height:1;letter-spacing:-.02em;margin:0}
.ui-card-description{font-size:.875rem;color:var(--muted-foreground);margin:0}
.ui-card-content{padding:0 1.5rem 1.5rem}
.ui-card-footer{display:flex;align-items:center;padding:0 1.5rem 1.5rem}
.ui-alert{position:relative;width:100%;border-radius:var(--radius);border:1px solid var(--border);padding:.75rem 1rem;font-size:.875rem}
.ui-alert-destructive{border-color:color-mix(in srgb,var(--destructive) 50%,transparent);color:var(--destructive)}
.ui-badge{display:inline-flex;align-items:center;border-radius:9999px;border:1px solid transparent;padding:.125rem .625rem;font-size:.75rem;font-weight:600;background:var(--primary);color:var(--primary-foreground)}
.ui-badge-secondary{background:var(--secondary);color:var(--secondary-foreground)}
.ui-badge-outline{background:transparent;border-color:var(--border);color:var(--foreground)}
.ui-scope *:not(.MuiCircularProgress-root):not(.MuiCircularProgress-root *):not(.ld-motion),.ui-scope *::before,.ui-scope *::after{animation:none !important}
.ui-sep{height:1px;width:100%;background:var(--border);border:0;margin:0}
`;

let injected = false;
const ensureStyles = () => {
  if (injected || typeof document === "undefined") return;
  const el = document.createElement("style");
  el.setAttribute("data-ui-shadcn", "");
  el.textContent = CSS;
  document.head.appendChild(el);
  injected = true;
};

const cx = (...a) => a.filter(Boolean).join(" ");

/** Wrap a design in this to get tokens; pass vars={{"--primary":"#0d9488", ...}} to theme it. */
export const ThemeScope = ({ vars, className, style, children, ...rest }) => {
  ensureStyles();
  return (
    <div className={cx("ui-scope", className)} style={{ ...vars, ...style }} {...rest}>
      {children}
    </div>
  );
};

export const Button = forwardRef(({ variant = "default", size, className, ...props }, ref) => {
  ensureStyles();
  return (
    <button
      ref={ref}
      className={cx("ui-btn", `ui-btn-${variant}`, size && `ui-btn-${size}`, className)}
      {...props}
    />
  );
});

export const Input = forwardRef(({ className, ...props }, ref) => {
  ensureStyles();
  return <input ref={ref} className={cx("ui-input", className)} {...props} />;
});

export const Label = ({ className, ...props }) => <label className={cx("ui-label", className)} {...props} />;

export const Card = forwardRef(({ className, ...p }, ref) => <div ref={ref} className={cx("ui-card", className)} {...p} />);
export const CardHeader = ({ className, ...p }) => <div className={cx("ui-card-header", className)} {...p} />;
export const CardTitle = ({ className, ...p }) => <h3 className={cx("ui-card-title", className)} {...p} />;
export const CardDescription = ({ className, ...p }) => <p className={cx("ui-card-description", className)} {...p} />;
export const CardContent = ({ className, ...p }) => <div className={cx("ui-card-content", className)} {...p} />;
export const CardFooter = ({ className, ...p }) => <div className={cx("ui-card-footer", className)} {...p} />;

export const Alert = ({ variant = "default", className, ...p }) => (
  <div role="alert" className={cx("ui-alert", variant === "destructive" && "ui-alert-destructive", className)} {...p} />
);
export const Badge = ({ variant = "default", className, ...p }) => (
  <span className={cx("ui-badge", variant !== "default" && `ui-badge-${variant}`, className)} {...p} />
);
export const Separator = ({ className, ...p }) => <hr className={cx("ui-sep", className)} {...p} />;
